/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {useMemo} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {BulkActionEntity, BulkActionType} from '@ion/core/src/types/entities';
import {BulkActions} from './types';

const TRANSLATION_KEYS = {
  [BulkActionType.MOVE]                 : 'move',
  [BulkActionType.GROUP_ASSIGNMENT]     : 'groupAssignment',
  [BulkActionType.JOB_CREATION]         : 'createJob',
  [BulkActionType.CANCEL_FROM_ALL_JOBS] : 'cancelFromAllJobs',
  [BulkActionType.SCHEDULE_DEVICE_SYNC] : 'scheduleDeviceSync',
  [BulkActionType.SERVICE_ENABLEMENT]   : 'serviceEnablement',
  [BulkActionType.UNPAIR]               : 'unpair',
  [BulkActionType.SCI_UPDATE]           : 'sciUpdate',
  [BulkActionType.BULK_EDIT]            : 'bulkEdit',
  [BulkActionType.BULK_ADD]             : 'bulkAdd',
  [BulkActionType.VBUS]                 : 'vBusStatus',
  [BulkActionType.ASSIGN_REGION]        : 'assignRegion',
  [BulkActionType.VEHICLE_PROFILE]      : 'vehicleProfile',
  [BulkActionType.DEVICE_PASSWORD]      : 'devicePassword'
};

const ENTITY_TO_ACTION_MAP = {
  [BulkActionEntity.DEVICES] : [
    BulkActionType.MOVE,
    BulkActionType.GROUP_ASSIGNMENT,
    BulkActionType.JOB_CREATION,
    BulkActionType.CANCEL_FROM_ALL_JOBS,
    BulkActionType.SCHEDULE_DEVICE_SYNC,
    BulkActionType.SERVICE_ENABLEMENT,
    BulkActionType.UNPAIR,
    BulkActionType.SCI_UPDATE,
    BulkActionType.BULK_EDIT,
    BulkActionType.BULK_ADD,
    BulkActionType.VBUS,
    BulkActionType.VEHICLE_PROFILE,
    BulkActionType.DEVICE_PASSWORD
  ],
  [BulkActionEntity.ASSETS] : [
    BulkActionType.MOVE,
    BulkActionType.GROUP_ASSIGNMENT,
    BulkActionType.UNPAIR,
    BulkActionType.BULK_EDIT,
    BulkActionType.VEHICLE_PROFILE
  ],
  [BulkActionEntity.ROLES] : [
    BulkActionType.MOVE
  ],
  [BulkActionEntity.INVENTORY] : [
    BulkActionType.ASSIGN_REGION
  ]
};

/**
 * Custom hook for maintaining & returning the corresponding allowed actions for the given entity.
 */
export const useEntityActions = (entity: BulkActionEntity): BulkActions => {
  const entityLabel = translate('common:entityLabels', {entity, returnObjects: true});

  const {
    actions: actionTranslations
  } = translate('common:editors.bulkActions.dialogBulkAction.actionSelection.actionList', {
    entity        : entityLabel[entity],
    returnObjects : true
  });

  const actions = useMemo(() => {
    const entityActions = ENTITY_TO_ACTION_MAP[entity];
    return entityActions.map(action => ({
      title : actionTranslations[TRANSLATION_KEYS[action]],
      type  : action
    }));
  }, [entity]);

  return actions;
};

export default useEntityActions;
